import * as Sentry from "@sentry/browser";
import { groundLevelIds } from "building-generation/elements/level/levelTools";
import { makeBlockPerimeterFromRoomOutlines } from "building-generation/generationTools/generateBlockPerimetersTools";
import type { Building } from "building-types/building/buildingTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import type { GeneratedSite } from "building-types/site/site";
import { BuiltInSiteType } from "building-types/site/site";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import { aabb3Overlaps } from "geom/aabb3";
import type { AABB3 } from "geom/aabb3Types";
import type { Face } from "geom/brep";
import { faceAABB3, faceNew } from "geom/brep";
import { DIST_EPSILON } from "geom/core";
import { intersectFaceFace } from "geom/intersect";
import { IntersectionKind } from "geom/intersectRay2";
import { planeNew, planeNormal, planeProject, planeSignedDistanceTo } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { offsetClosedPolyLine2 } from "geom/polyclip/offsetClosedPolyLine2";
import { polyLine2AABB2, polyLine2Copy, polyLine2CopySimplify, polyLine2SetCCW, polyLine2SetCW } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { trimPolyLine2 } from "geom/trim";
import { vec2Dot } from "geom/vec2";

/** How far the grade line runs past each end of the building footprint. */
export const GRADE_LINE_FOOTPRINT_MARGIN_INCHES = 36;
/** Bridges small gaps between adjacent facade faces at wall-mass seams into one continuous run. */
const BRIDGE_EPSILON_INCHES = 6;
/** Drops degenerate near-zero-length segments produced at face-loop boundaries. */
const MIN_SEGMENT_LENGTH_INCHES = 0.5;
/**
 * Cap on the slope carried past the building corners. The last inch of the intersection can be
 * near-vertical where grade meets a step or a retaining condition; running that slope out three
 * feet would draw a spike rather than ground.
 */
const MAX_EXTENSION_SLOPE = 0.5;
/**
 * Unit-normal dot threshold for "faces the camera" / "is not a vertical face". Chosen over
 * DIST_EPSILON because these are direction comparisons, not distances.
 */
const NORMAL_EPSILON = 1e-6;
/** How far the terrain-probe curtain overshoots the terrain, so grazing contact still registers. */
const CURTAIN_Z_OVERSHOOT_INCHES = 12;
/**
 * How far outside the footprint to probe. Building generation cuts the terrain along exactly this
 * outline, so probing on the seam grazes the ground surface's own boundary and intersections drop
 * out. Stepping an inch out samples solid ground just beyond the building instead.
 */
const FOOTPRINT_PROBE_OFFSET_INCHES = 1;
/**
 * Max depth range one curtain may span before its edge is probed in pieces. Bounds the midpoint
 * depth key's error to half this, so walls overlapping in `u` but closer than this in plan can
 * still be occluded in the wrong order.
 */
const MAX_FACADE_DEPTH_SPAN_INCHES = 6;
/**
 * Morphological-close radius for the footprint; see `closeFootprintSlits`. Collapses necks under
 * twice this width, which real notches in a building exceed.
 */
const FOOTPRINT_SLIT_CLOSE_INCHES = 3;
/**
 * Shortest flat-array length that encloses area: four points, the last repeating the first.
 * Anything shorter survives the winding filters but makes the polygon offsetter throw.
 */
const MIN_CLOSED_LOOP_LENGTH = 8;
/** Two points, so anything shorter is not a drawable polyline. */
const MIN_DRAWABLE_POLYLINE_LENGTH = 4;

/**
 * Per-session cap on Sentry reports per failure kind. The reporting sites sit on the render path,
 * so unbounded reporting would fire per keystroke, but one per kind would let the first bad
 * document hide every later one during rollout.
 */
const MAX_REPORTS_PER_FAILURE_KIND = 5;

const failureReportCounts = new Map<string, number>();

function reportGradeLineFailure(kind: string, error: unknown): void {
  const seen = failureReportCounts.get(kind) ?? 0;
  if (seen >= MAX_REPORTS_PER_FAILURE_KIND) {
    return;
  }
  failureReportCounts.set(kind, seen + 1);
  Sentry.captureException(error);
}

export interface FacadeFace {
  readonly face: Face;
  /** Signed distance from viewPlane at the curtain's midpoint -- larger means closer to camera. */
  readonly depth: number;
}

interface ElevationFacadeFaces {
  readonly facadeFaces: FacadeFace[];
  readonly footprintUMin: number;
  readonly footprintUMax: number;
}

/**
 * The building footprint at grade: the traced perimeter of the ground level's room outlines.
 * Traced rather than unioned -- a boolean union leaves hairline slits between adjacent rooms, and
 * their sides read as camera-facing facade that draws grade deep inside the house.
 */
function computeBuildingFootprintOutlines(building: Building): PolyLine2[] {
  // `groundLevelIds`, plural: datum-placed levels can give a house several ground levels, and
  // taking only the lowest drops whole masses (a stepped-down garage) out of the footprint.
  const outlines = groundLevelIds(building.levels)
    .flatMap((levelId) => roomsOnLevel(levelId, building.rooms, building.roomsByLevel))
    .filter((room) => room.type !== RoomType.BAY_WINDOW && room.outline.length >= MIN_CLOSED_LOOP_LENGTH)
    .map((room) => room.outline);

  if (outlines.length === 0) {
    return [];
  }

  // Outward-normal test in the caller assumes CCW.
  return closeFootprintSlits(
    makeBlockPerimeterFromRoomOutlines(outlines).filter((pl) => pl.length >= MIN_CLOSED_LOOP_LENGTH),
  );
}

/** Memo, on the same reasoning as `groundTerrainBySite` below. */
const footprintOutlinesByBuilding = new WeakMap<Building, PolyLine2[]>();

/** Cached; the footprint depends on the building alone. Exported as a test seam. */
export function getBuildingFootprintOutlines(building: Building): PolyLine2[] {
  const cached = footprintOutlinesByBuilding.get(building);
  if (cached) {
    return cached;
  }

  const outlines = computeBuildingFootprintOutlines(building);
  footprintOutlinesByBuilding.set(building, outlines);
  return outlines;
}

/**
 * Closes one traced loop, returning `undefined` if the offsetter cannot handle it.
 *
 * One loop at a time: `offsetClosedPolyLine2` reads an array of loops as a polygon-with-holes, so
 * closing the whole trace in one call silently deletes detached masses (a house plus garage came
 * back as the house alone). It also throws on the degenerate input a planar-graph trace is full of,
 * so simplify first and catch, or one bad loop loses the whole elevation viewport.
 */
function tryCloseOneLoop(loop: PolyLine2): PolyLine2[] | undefined {
  try {
    // `offsetClosedPolyLine2` expects a CW outer loop.
    const grown = offsetClosedPolyLine2([polyLine2SetCW(polyLine2CopySimplify(loop))], FOOTPRINT_SLIT_CLOSE_INCHES)
      .flat()
      // A single loop can legitimately grow into several; keep them all.
      .filter((pl) => pl.length >= MIN_CLOSED_LOOP_LENGTH);
    if (grown.length === 0) {
      // Reported like the throw below: empty is the likelier rejection and carries the same signal.
      reportGradeLineFailure(
        "closeFootprintSlits:emptyGrow",
        new Error("Modeled grade line: outward footprint offset returned nothing for a traced loop"),
      );
      return undefined;
    }

    const shrunk = offsetClosedPolyLine2(grown, -FOOTPRINT_SLIT_CLOSE_INCHES)
      .flat()
      .filter((pl) => pl.length >= MIN_CLOSED_LOOP_LENGTH);
    if (shrunk.length === 0) {
      reportGradeLineFailure(
        "closeFootprintSlits:emptyShrink",
        new Error(`Modeled grade line: inward footprint offset returned nothing for ${grown.length} grown loop(s)`),
      );
      return undefined;
    }

    return shrunk;
  } catch (error) {
    // The fallback still draws, but a rejected footprint may carry slits, so it is worth seeing.
    reportGradeLineFailure("closeFootprintSlits:threw", error);
    return undefined;
  }
}

/**
 * Morphological close of the footprint: offset out, then back in. Collapses the hairline slits left
 * where adjacent rooms' outlines do not quite tile, whose sides would otherwise read as
 * camera-facing facade. Per loop, with a per-loop fallback; see `tryCloseOneLoop`.
 */
export function closeFootprintSlits(traced: PolyLine2[]): PolyLine2[] {
  if (traced.length === 0) {
    return [];
  }

  const closed: PolyLine2[] = [];
  for (const loop of traced) {
    for (const pl of tryCloseOneLoop(loop) ?? [loop]) {
      closed.push(pl);
    }
  }

  return closed.filter((pl) => pl.length >= MIN_CLOSED_LOOP_LENGTH).map((pl) => polyLine2SetCCW(polyLine2Copy(pl)));
}

/**
 * How many pieces one camera-facing footprint edge is probed as. One for an edge square to the view
 * plane (every edge of an orthogonal footprint: view planes are cardinal, so `depthSpan` is exactly
 * zero), otherwise enough to bound each piece's depth span -- clamped so no piece is thinner in `u`
 * than the slivers the occlusion pass discards, or a nearly edge-on edge would split into pieces
 * that all drop out and stop contributing ground at all.
 */
function facadeSubEdgeCount(depthSpan: number, uSpan: number): number {
  const wanted = Math.ceil(Math.abs(depthSpan) / MAX_FACADE_DEPTH_SPAN_INCHES);
  if (wanted <= 1) {
    return 1;
  }
  const affordable = Math.floor(Math.abs(uSpan) / MIN_SEGMENT_LENGTH_INCHES);
  return Math.max(1, Math.min(wanted, affordable));
}

/**
 * The terrain-probe curtains for one camera-facing footprint edge, in one or more pieces. Pieces
 * are collinear and meet the ground along the same line the whole edge would have; only the depth
 * key differs, and they are disjoint in `u` so pieces of one edge never compete in the occlusion
 * sort. Exported as a test seam. Callers must reject zero-length edges first.
 */
export function footprintEdgeFacadeFaces(
  viewPlane: Plane,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): FacadeFace[] {
  const [viewX, viewY] = planeNormal(viewPlane);
  const dx = x1 - x0;
  const dy = y1 - y0;
  const length = Math.hypot(dx, dy);
  // Outlines are CCW, so the interior lies left of the edge and the outward normal is right.
  const outX = dy / length;
  const outY = -dx / length;

  const [u0] = planeProject(viewPlane, x0, y0, 0);
  const [u1] = planeProject(viewPlane, x1, y1, 0);
  const subEdgeCount = facadeSubEdgeCount(dx * viewX + dy * viewY, u1 - u0);
  const subEdgeLength = length / subEdgeCount;

  const faces: FacadeFace[] = [];
  for (let sub = 0; sub < subEdgeCount; sub++) {
    const sx0 = x0 + (dx * sub) / subEdgeCount;
    const sy0 = y0 + (dy * sub) / subEdgeCount;
    const sx1 = x0 + (dx * (sub + 1)) / subEdgeCount;
    const sy1 = y0 + (dy * (sub + 1)) / subEdgeCount;

    // Offset along the edge's shared outward normal, so the pieces stay collinear; their shared
    // endpoints agree to float noise, far inside the bridging epsilon.
    const px0 = sx0 + outX * FOOTPRINT_PROBE_OFFSET_INCHES;
    const py0 = sy0 + outY * FOOTPRINT_PROBE_OFFSET_INCHES;
    const px1 = sx1 + outX * FOOTPRINT_PROBE_OFFSET_INCHES;
    const py1 = sy1 + outY * FOOTPRINT_PROBE_OFFSET_INCHES;

    // A unit-height sliver in the edge's plane. `terrainProbeFace` stretches it over the
    // terrain's full vertical range when the intersection runs.
    const plane = planeNew(px0, py0, 0, dx / length, dy / length, 0, 0, 0, 1);
    faces.push({
      face: faceNew([polyLine2New([0, 0, subEdgeLength, 0, subEdgeLength, 1, 0, 1, 0, 0])], plane),
      // Keyed where the curtain actually sits, offset included: keying the un-offset footprint
      // midpoint biases every curtain by an orientation-dependent fraction of the probe offset.
      depth: planeSignedDistanceTo(viewPlane, (px0 + px1) / 2, (py0 + py1) / 2, 0),
    });
  }
  return faces;
}

/**
 * Turns the camera-facing edges of the building footprint into vertical curtains to probe the
 * ground with, plus the footprint's horizontal extent along the view axis (`u`).
 *
 * Working from footprint edges rather than wall faces is what lets a porch slab or a stoop
 * contribute to the grade line: the grade line marks where the ground meets *the building*, and
 * plenty of building has no wall at its perimeter.
 */
function getElevationFacadeFaces(building: Building, viewPlane: Plane): ElevationFacadeFaces | undefined {
  const [viewX, viewY] = planeNormal(viewPlane);

  const facadeFaces: FacadeFace[] = [];
  let footprintUMin = Infinity;
  let footprintUMax = -Infinity;

  for (const outline of getBuildingFootprintOutlines(building)) {
    for (let i = 0; i + 3 < outline.length; i += 2) {
      const [x0, y0, x1, y1] = [outline[i], outline[i + 1], outline[i + 2], outline[i + 3]];
      const dx = x1 - x0;
      const dy = y1 - y0;
      const length = Math.hypot(dx, dy);
      if (length < MIN_SEGMENT_LENGTH_INCHES) {
        continue;
      }

      // Outlines are CCW, so the interior lies left of the edge and the outward normal is right.
      const outX = dy / length;
      const outY = -dx / length;
      if (vec2Dot(viewX, viewY, outX, outY) < NORMAL_EPSILON) {
        continue;
      }

      // The camera-facing span rather than the true silhouette -- accepted, since the margin is a
      // drafting allowance. Measured from the edge's own endpoints so splitting cannot move it.
      const [u0] = planeProject(viewPlane, x0, y0, 0);
      const [u1] = planeProject(viewPlane, x1, y1, 0);
      footprintUMin = Math.min(footprintUMin, u0, u1);
      footprintUMax = Math.max(footprintUMax, u0, u1);

      // One at a time; a spread can exceed the engine's argument-count limit.
      for (const facadeFace of footprintEdgeFacadeFaces(viewPlane, x0, y0, x1, y1)) {
        facadeFaces.push(facadeFace);
      }
    }
  }

  if (facadeFaces.length === 0 || !isFinite(footprintUMin) || !isFinite(footprintUMax)) {
    return undefined;
  }

  return { facadeFaces, footprintUMin, footprintUMax };
}

/**
 * Every site surface that counts as "ground" for grade-line purposes.
 *
 * `topographyGeom` alone is not enough: generation subtracts the room outlines from the terrain, so
 * it has a hole exactly where the building sits, and the cut earth lands in `grassGeom`. Hardscape
 * is ground too. Vertical faces are excluded so the terrain skirt and the subtraction hole's walls
 * don't draw a second line far below the real one.
 */
function computeGroundFacesForGradeLine(site: GeneratedSite): Face[] {
  // `sitePlaneGeom` is excluded: a 50000" backdrop parked far below the lot. Only white box sites
  // put their ground in `siteWhiteBoxGeom`; the other types fill `topographyGeom`.
  const groundGroup = site.type === BuiltInSiteType.WHITE_BOX ? site.siteWhiteBoxGeom : site.topographyGeom;

  const faces = groundGroup.toFaces();
  // `curbGeom` is excluded: on a contour site it holds retaining walls rather than curbs, and a
  // retaining wall's top cap shares the facade's depth exactly, so the equal-depth tie-break below
  // would step the grade line onto it.
  for (const list of [site.grassGeom, site.drivewayGeom, site.sidewalkGeom, site.roadGeom]) {
    for (const entry of list) {
      // One at a time; a contour site's tag group can exceed the engine's argument-count limit.
      for (const face of entry.group.toFaces()) {
        faces.push(face);
      }
    }
  }

  return faces.filter((face) => Math.abs(planeNormal(face.plane)[2]) > NORMAL_EPSILON);
}

/**
 * A ground-face set with one bounding box per face, index-aligned, plus the set's vertical range.
 * Bundled so the boxes are built once per site rather than once per view, and so faces and boxes
 * cannot be paired wrong -- a mismatch would silently drop grade-line runs, not throw. Treat the
 * boxes as immutable: they are shared across every later view of the site.
 */
export interface GroundTerrain {
  readonly faces: Face[];
  readonly faceAABBs: readonly AABB3[];
  /** Lowest and highest z over all faces; `Infinity` / `-Infinity` when there are none. */
  readonly zMin: number;
  readonly zMax: number;
}

/** Bounds a ground-face set in one pass; a spread over the extremes can exceed the engine's argument-count limit. */
function groundTerrainNew(faces: Face[]): GroundTerrain {
  const faceAABBs: AABB3[] = [];
  let zMin = Infinity;
  let zMax = -Infinity;
  for (const face of faces) {
    const aabb = faceAABB3(face);
    faceAABBs.push(aabb);
    zMin = Math.min(zMin, aabb.minZ);
    zMax = Math.max(zMax, aabb.maxZ);
  }

  return { faces, faceAABBs, zMin, zMax };
}

/**
 * Keyed on the site's identity: a fresh `generateBuilding` produces a fresh `GeneratedSite`, so
 * invalidation is automatic and eviction is the garbage collector's problem. Per site rather than
 * per building, so swapping one site invalidates only that site's terrain.
 */
const groundTerrainBySite = new WeakMap<GeneratedSite, GroundTerrain>();

/**
 * Cached: this runs twice per elevation view (the slow pass re-runs the fast one to populate the
 * collision system) while depending only on the site.
 */
export function getGroundTerrainForGradeLine(site: GeneratedSite): GroundTerrain {
  const cached = groundTerrainBySite.get(site);
  if (cached) {
    return cached;
  }

  const terrain = groundTerrainNew(computeGroundFacesForGradeLine(site));
  groundTerrainBySite.set(site, terrain);
  return terrain;
}

/** The faces alone; a test seam for asserting the ground-set rules without the bundle in the way. */
export function getGroundFacesForGradeLine(site: GeneratedSite): Face[] {
  return getGroundTerrainForGradeLine(site).faces;
}

/**
 * Every enabled site's ground as one terrain set. The single-site case returns the cached set
 * untouched. Several enabled sites is a shipped configuration (an always-on dynamic site plus an
 * option-gated white box site), and concatenating copies references without re-bounding anything.
 * Exported for the alignment test, since a misaligned box shows only as a silently missing run.
 */
export function getBuildingGroundTerrain(building: Building): GroundTerrain {
  const sites = Object.values(building.sites ?? {});
  if (sites.length === 1) {
    return getGroundTerrainForGradeLine(sites[0]);
  }

  const faces: Face[] = [];
  const faceAABBs: AABB3[] = [];
  let zMin = Infinity;
  let zMax = -Infinity;
  for (const site of sites) {
    const terrain = getGroundTerrainForGradeLine(site);
    for (let i = 0; i < terrain.faces.length; i++) {
      faces.push(terrain.faces[i]);
      faceAABBs.push(terrain.faceAABBs[i]);
    }
    zMin = Math.min(zMin, terrain.zMin);
    zMax = Math.max(zMax, terrain.zMax);
  }

  return { faces, faceAABBs, zMin, zMax };
}

/**
 * Builds a full-height "curtain" in the facade face's plane, spanning its horizontal extent and the
 * terrain's whole vertical range. The grade line is where ground meets the wall *line*, not where it
 * cuts the wall *panel* -- on a normal lot the ground sits below the wall, so a panel probe finds
 * nothing and picks up only walls the terrain happens to slice through.
 */
function terrainProbeFace(face: Face, terrainZMin: number, terrainZMax: number): Face | undefined {
  const { uZ, vZ, originZ } = face.plane;

  // One of the plane's two axes runs vertically on a wall face; the other spans the wall's length.
  const verticalIsV = Math.abs(vZ) >= Math.abs(uZ);
  const verticalAxisZ = verticalIsV ? vZ : uZ;
  if (Math.abs(verticalAxisZ) < NORMAL_EPSILON) {
    return undefined;
  }

  let alongMin = Infinity;
  let alongMax = -Infinity;
  for (const loop of face.loops) {
    for (let i = 0; i < loop.length; i += 2) {
      const along = verticalIsV ? loop[i] : loop[i + 1];
      alongMin = Math.min(alongMin, along);
      alongMax = Math.max(alongMax, along);
    }
  }
  if (!isFinite(alongMin) || alongMax - alongMin < DIST_EPSILON) {
    return undefined;
  }

  const lowT = (terrainZMin - CURTAIN_Z_OVERSHOOT_INCHES - originZ) / verticalAxisZ;
  const highT = (terrainZMax + CURTAIN_Z_OVERSHOOT_INCHES - originZ) / verticalAxisZ;
  const vertMin = Math.min(lowT, highT);
  const vertMax = Math.max(lowT, highT);

  const at = (along: number, vert: number): [number, number] => (verticalIsV ? [along, vert] : [vert, along]);
  return faceNew(
    [
      polyLine2New([
        ...at(alongMin, vertMin),
        ...at(alongMax, vertMin),
        ...at(alongMax, vertMax),
        ...at(alongMin, vertMax),
        ...at(alongMin, vertMin),
      ]),
    ],
    face.plane,
  );
}

interface USegment {
  u0: number;
  z0: number;
  u1: number;
  z1: number;
  readonly depth: number;
}

/**
 * Height of a segment at `u`. Guarded because a facade edge running nearly parallel to the view
 * direction can produce a segment with `u1` ~ `u0`.
 */
function zAt(seg: USegment, u: number): number {
  const du = seg.u1 - seg.u0;
  if (Math.abs(du) < DIST_EPSILON) {
    return seg.z0;
  }
  return seg.z0 + (seg.z1 - seg.z0) * ((u - seg.u0) / du);
}

/**
 * Subtracts a sorted, non-overlapping set of claimed [min, max) ranges from [u0, u1). Slivers below
 * `MIN_SEGMENT_LENGTH_INCHES` are dropped: walls meeting at a corner differ only by float noise.
 */
function subtractClaimedRanges(u0: number, u1: number, claimed: [number, number][]): [number, number][] {
  const result: [number, number][] = [];
  const keep = (min: number, max: number) => {
    if (max - min >= MIN_SEGMENT_LENGTH_INCHES) {
      result.push([min, max]);
    }
  };

  let cursor = u0;
  for (const [claimMin, claimMax] of claimed) {
    // `claimed` is sorted by start, so once a claim begins past the range every later one does too.
    if (claimMin >= u1) {
      break;
    }
    if (claimMax <= cursor) {
      continue;
    }
    if (claimMin > cursor) {
      keep(cursor, Math.min(claimMin, u1));
    }
    cursor = Math.max(cursor, claimMax);
    if (cursor >= u1) {
      break;
    }
  }
  if (cursor < u1) {
    keep(cursor, u1);
  }
  return result;
}

/** First index in a sorted range list at which `predicate` becomes true, or `length` if never. */
function firstIndexWhere(ranges: readonly [number, number][], predicate: (range: [number, number]) => boolean): number {
  let low = 0;
  let high = ranges.length;
  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (predicate(ranges[mid])) {
      high = mid;
    } else {
      low = mid + 1;
    }
  }
  return low;
}

/**
 * Adds [u0, u1] to a sorted, non-overlapping claim list, absorbing every range it touches. Binary
 * search rather than push-and-resort, which would make the occlusion pass superlinear.
 */
function insertAndMergeClaim(claimed: [number, number][], u0: number, u1: number): void {
  const first = firstIndexWhere(claimed, (range) => range[1] >= u0);
  const end = firstIndexWhere(claimed, (range) => range[0] > u1);

  if (first === end) {
    claimed.splice(first, 0, [u0, u1]);
    return;
  }

  // Everything in [first, end) touches the new range, so the union of all of them is one range.
  const merged: [number, number] = [Math.min(u0, claimed[first][0]), Math.max(u1, claimed[end - 1][1])];
  claimed.splice(first, end - first, merged);
}

/**
 * Intersects the facade curtains against the terrain, resolving curtains that overlap in `u` (a
 * recessed wall behind a bump-out) by depth -- nearest to the camera wins. Returns disjoint
 * polylines in (u, z) model space, ordered by u. Terrain may arrive bare or pre-bounded as a
 * `GroundTerrain`; the production path passes the latter so boxes are built once per site.
 */
export function intersectFacadeWithTerrain(
  facadeFaces: FacadeFace[],
  terrain: Face[] | GroundTerrain,
  viewPlane: Plane,
): PolyLine2[] {
  const {
    faces: terrainFaces,
    faceAABBs: terrainAABBs,
    zMin: terrainZMin,
    zMax: terrainZMax,
  } = Array.isArray(terrain) ? groundTerrainNew(terrain) : terrain;

  // `GroundTerrain` is exported, so check the alignment its doc promises: the loop below indexes
  // both lists in lockstep, and a short box list would read as `undefined` inside `aabb3Overlaps`.
  if (terrainFaces.length !== terrainAABBs.length) {
    throw new Error(
      `Modeled grade line: ${terrainFaces.length} terrain faces against ${terrainAABBs.length} bounding boxes.`,
    );
  }

  const intersectionErrors: string[] = [];
  const rawSegments: USegment[] = [];
  for (const { face, depth } of facadeFaces) {
    const facadeFace = terrainProbeFace(face, terrainZMin, terrainZMax);
    if (!facadeFace) {
      continue;
    }

    const facadeAABB = faceAABB3(facadeFace);
    // A full linear scan; not the dominating cost here. A small uniform grid over the boxes (not an
    // R-tree, whose build costs more than it saves at this query count) is the follow-up that pays,
    // especially on canted footprints and larger lots -- but the sort below must become a total
    // order first, since any index changes visit order. Note the z part of the box test can never
    // reject: the curtain's span is derived from the extremes of this very box list.
    for (let i = 0; i < terrainFaces.length; i++) {
      if (!aabb3Overlaps(facadeAABB, terrainAABBs[i])) {
        continue;
      }

      // Collected rather than dropped: both of `intersectFaceFace`'s real failures show up as a
      // silent gap in the grade line. Reported once per view below, not once per face.
      const result = intersectFaceFace(facadeFace, terrainFaces[i], intersectionErrors);
      if (!result || result.kind !== IntersectionKind.TRANSVERSE) {
        continue;
      }

      for (const seg of result.segments) {
        const [u0, z0] = planeProject(viewPlane, seg.x0, seg.y0, seg.z0);
        const [u1, z1] = planeProject(viewPlane, seg.x1, seg.y1, seg.z1);
        if (Math.hypot(u1 - u0, z1 - z0) < MIN_SEGMENT_LENGTH_INCHES) {
          continue;
        }
        rawSegments.push(u0 <= u1 ? { u0, z0, u1, z1, depth } : { u0: u1, z0: z1, u1: u0, z1: z0, depth });
      }
    }
  }

  if (intersectionErrors.length > 0) {
    reportGradeLineFailure(
      `intersectFaceFace:${intersectionErrors[0]}`,
      new Error(
        `Modeled grade line: ${intersectionErrors.length} facade/terrain intersection failure(s). ` +
          `First: ${intersectionErrors[0]}`,
      ),
    );
  }

  if (rawSegments.length === 0) {
    return [];
  }

  // Nearest-to-camera first, so each segment only loses ground already claimed by a nearer one. At
  // equal depth (one curtain crossing several terrain faces) the higher crossing is the grade.
  //
  // That equal-depth tie is what hides the pad and hardscape undersides the vertical-face filter
  // keeps: both caps of a solid are cut by the same curtain, so their depths are bit-identical and
  // the z comparison decides. Pieces of one edge are disjoint in `u` and never compete, but a depth
  // that varied *within* one curtain would break the tie and needs an explicit "drop faces below
  // the topography surface" filter instead. Where the upper surface has a gap the lower one shows
  // through regardless; an up-facing-only ground filter would close that, but changes behaviour on
  // white box and dynamic sites, so it wants its own change.
  //
  // Not a total order: exact ties keep terrain-face order, so output depends on visit order at the
  // 1e-3" level. Making it total is the prerequisite for any broadphase.
  rawSegments.sort((a, b) => b.depth - a.depth || b.z0 + b.z1 - (a.z0 + a.z1));

  const claimed: [number, number][] = [];
  const visible: USegment[] = [];
  for (const seg of rawSegments) {
    for (const [u0, u1] of subtractClaimedRanges(seg.u0, seg.u1, claimed)) {
      visible.push({ u0, z0: zAt(seg, u0), u1, z1: zAt(seg, u1), depth: seg.depth });
    }
    // Claimed from the intersection segment, not the facade face: a nearer wall with no ground
    // contact claims nothing, so a farther wall's grade shows through. Claiming the face's full
    // span would erase the grade behind a bump-out that never touches ground, which is worse.
    insertAndMergeClaim(claimed, seg.u0, seg.u1);
  }

  visible.sort((a, b) => a.u0 - b.u0);

  // Genuine gaps in the ground contact stay disjoint; only sub-epsilon seams merge.
  const runs: number[][] = [];
  for (const seg of visible) {
    const lastRun = runs[runs.length - 1];
    const lastU = lastRun?.[lastRun.length - 2];
    if (lastRun && lastU !== undefined && seg.u0 - lastU <= BRIDGE_EPSILON_INCHES) {
      lastRun.push(seg.u0, seg.z0, seg.u1, seg.z1);
    } else {
      runs.push([seg.u0, seg.z0, seg.u1, seg.z1]);
    }
  }

  return runs.map((flat) => polyLine2New(flat));
}

/** A point strictly inside [xMin, xMax], for either bound being infinite. */
function selectionXInRange(xMin: number, xMax: number): number {
  if (isFinite(xMin) && isFinite(xMax)) {
    return (xMin + xMax) / 2;
  }
  if (isFinite(xMax)) {
    return xMax - 1;
  }
  return xMin + 1;
}

/**
 * Clips a grade-line profile to an x-range via `trimPolyLine2`: with a vertical line at each finite
 * bound and a selection point inside the range, the piece it removes is the piece to keep. Two
 * caveats: with no crossings it hands back the whole subject whichever side it sits on, hence the
 * extent check; and an unbounded side needs a finite trimming line scaled to the profile's height,
 * since a very long one loses the intersection to float conditioning.
 */
export function clipProfileToXRange(pl: PolyLine2, xMin: number, xMax: number): PolyLine2 | undefined {
  const { minY, maxY } = polyLine2AABB2(pl);
  const lo = minY - 1;
  const hi = maxY + 1;

  const trimmingPaths: PolyLine2[] = [];
  if (isFinite(xMin)) {
    trimmingPaths.push(polyLine2New([xMin, lo, xMin, hi]));
  }
  if (isFinite(xMax)) {
    trimmingPaths.push(polyLine2New([xMax, lo, xMax, hi]));
  }
  if (trimmingPaths.length === 0) {
    return pl;
  }

  const selectionX = selectionXInRange(xMin, xMax);
  const [kept] = trimPolyLine2(pl, trimmingPaths, selectionX, (lo + hi) / 2).removed;
  // The length check is defensive: trim filters zero-length pieces itself, so it cannot fire today.
  if (!kept || kept.length < MIN_DRAWABLE_POLYLINE_LENGTH) {
    return undefined;
  }

  const keptBox = polyLine2AABB2(kept);
  if (keptBox.minX < xMin - DIST_EPSILON || keptBox.maxX > xMax + DIST_EPSILON) {
    return undefined;
  }
  return kept;
}

/** Slope of a polyline's end edge in (u, z), clamped so a near-vertical end can't draw a spike. */
function endEdgeSlope(uInner: number, zInner: number, uOuter: number, zOuter: number): number {
  const du = uOuter - uInner;
  if (Math.abs(du) < DIST_EPSILON) {
    return 0;
  }
  const slope = (zOuter - zInner) / du;
  return Math.max(-MAX_EXTENSION_SLOPE, Math.min(MAX_EXTENSION_SLOPE, slope));
}

/**
 * Runs the grade line a fixed margin past each end of the building, continuing the slope it
 * already has there. The facade/terrain intersection by construction stops dead at the building
 * corners, but an elevation is expected to show a few feet of ground to either side.
 */
export function extendGradeLinePastFootprint(pl: PolyLine2, margin: number, atStart: boolean): PolyLine2 {
  const n = pl.length;
  if (n < 4 || margin <= 0) {
    return pl;
  }

  if (atStart) {
    const slope = endEdgeSlope(pl[2], pl[3], pl[0], pl[1]);
    return polyLine2New([pl[0] - margin, pl[1] - slope * margin, ...pl]);
  }

  const slope = endEdgeSlope(pl[n - 4], pl[n - 3], pl[n - 2], pl[n - 1]);
  return polyLine2New([...pl, pl[n - 2] + margin, pl[n - 1] + slope * margin]);
}

/**
 * Computes the "Modeled" grade line: where the site's finished ground meets the building footprint,
 * trimmed to a margin past it. Returns `undefined` when there is nothing to compute against, so the
 * caller can fall back to the flat default line.
 *
 * A building floating clear above the ground still draws a line at the ground below it, whatever
 * the gap: the footprint carries no z, and the curtain's vertical span comes from the terrain
 * alone. Suppressing that would take an explicit proximity check, not a tweak here.
 */
export function computeModeledGradeLinePolyLines(building: Building, viewPlane: Plane): PolyLine2[] | undefined {
  const groundTerrain = getBuildingGroundTerrain(building);
  if (groundTerrain.faces.length === 0) {
    return undefined;
  }

  const facadeResult = getElevationFacadeFaces(building, viewPlane);
  if (!facadeResult) {
    return undefined;
  }

  const polyLines = intersectFacadeWithTerrain(facadeResult.facadeFaces, groundTerrain, viewPlane);
  if (polyLines.length === 0) {
    return undefined;
  }

  const uMin = facadeResult.footprintUMin - GRADE_LINE_FOOTPRINT_MARGIN_INCHES;
  const uMax = facadeResult.footprintUMax + GRADE_LINE_FOOTPRINT_MARGIN_INCHES;

  const clipped = polyLines
    .map((pl) => clipProfileToXRange(pl, uMin, uMax))
    .filter((pl): pl is PolyLine2 => pl !== undefined);
  if (clipped.length === 0) {
    return undefined;
  }

  // Only the outermost ends extend; interior run ends bound real gaps in the ground contact and
  // stay where the model puts them. Runs arrive ordered by u, and clipping preserves that.
  const lastIndex = clipped.length - 1;
  clipped[0] = extendGradeLinePastFootprint(clipped[0], GRADE_LINE_FOOTPRINT_MARGIN_INCHES, true);
  clipped[lastIndex] = extendGradeLinePastFootprint(clipped[lastIndex], GRADE_LINE_FOOTPRINT_MARGIN_INCHES, false);

  return clipped;
}
